import { Component, HostListener, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { MatIconRegistry } from '@angular/material/icon';

import { TranslateService } from '@ngx-translate/core';
import { I18nService } from './core/i18n.service';
import { ModuleMessageService } from './core/module-message.service';
import { ModuleStateService } from './module-state.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LoaderComponent } from "./shared/loader/loader.component";
import { AuthenticationService } from './core/authentication/authentication.service';
import { ApiService } from './core/api/api.service';

import { environment } from '../../../commondata/environments/environment';
import { skip } from 'rxjs';

import { CredentialsRequestMessage, GetInitialStateMessage } from '../../../commondata/messages/messages-dto';

import * as itIT from '../translations/it-IT.json';
import * as enGB from '../translations/en-GB.json';
import { LoaderService } from './shared/loader/loader.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'maps';

  @HostListener('window:message', ['$event'])
  onMessage(event: any) {
    this.moduleMessageService.handleMessage(event);
  }
  private moduleMessageService = inject(ModuleMessageService);
  private i18nService = inject(I18nService);
  private translateService = inject(TranslateService);
  private matIconRegistry = inject(MatIconRegistry);
  private loaderService = inject(LoaderService)

  constructor(
    private route: ActivatedRoute,
    private moduleStateService: ModuleStateService,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private authenticationService: AuthenticationService,
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    // Setup translations
    const translations: any = { 'it-IT': itIT, 'en-GB': enGB };
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages,
      translations
    );
    environment.supportedLanguages.forEach((element) => {
      this.translateService.getTranslation(element);
    });

    // Set default icon stile
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');
    this.registerIcons();

    this.loaderService.startLoading('generic', 'PLEASE_WAIT');

    // Viene usata la skip perche' il primo subscribe viene lanciato al caricamento del componente con valori vuoti
    this.route.queryParamMap.pipe(skip(1)).subscribe(
      params => {
        const guid = params.get('guid') ?? '';
        if (guid) {
          this.moduleStateService.guid = guid;
          this.moduleStateService.docId = params.get('id') ?? '';
          // Segna apertura documento
          this.apiService.addCustomDocStat(this.moduleStateService.docId, 'STAT_VIEWSHARED', this.authenticationService.getUserEmail()).subscribe(() => {
            console.log('Doc open');
          });
          // Inizializza messaggi con la shell
          // Verifica forzatura open in map view (con parametro openInView=true)
          const openInView = (params.get('openInView') || '') === 'true';
          const startText = params.get('startText') || '';
          const noUser = params.get('noUser') === 'true';
          const showMap = (params.get('showMap') || '') === 'true';
          const isNew = (params.get('isNew') || '') === 'true';
          const isLocked = (params.get('isLocked') || ' ') === 'true';
          this.moduleStateService.startText = startText;
          this.moduleStateService.isNew = isNew;
          this.moduleMessageService.initialize(this.moduleStateService.guid, openInView, showMap, isNew, noUser, isLocked, startText);
          if (noUser) {
            let url = 'map-view';
            if (showMap) {
              url = 'map-show';
            }
            if (this.authenticationService.isAuthenticated()) {
              this.authenticationService.logout();
            }
            this.router.navigate([url], { replaceUrl: true });
          } else {
            // Recupera le credenziali dalla shell
            const msg = new CredentialsRequestMessage(
              this.moduleStateService.guid,
              {}
            );
            (window as any).top.postMessage(msg, '*');
          }
          this.moduleMessageService.sendMessage(new GetInitialStateMessage(this.moduleStateService.guid));
        }
      });
  }

  private registerIcons() {
    this.matIconRegistry.addSvgIcon(
      "latex",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/latex.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "facebookIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/facebook.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "classroomIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/classroom.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "googleIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/google.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "wikipediaIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/wikipedia.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "youtubeIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/youtube.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "image-edit-outline",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/image-edit-outline.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "selection-search",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/selection-search.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "open_node",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/apri_nodi.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "hide_node",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/toolbar/nascondi_nodi.svg")
    );

  }

}